import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building,
  DollarSign,
  Bed,
  Building2,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import logo from "./images/logo.png";
import { Link } from "react-router-dom";

const AnimatedCounter = ({ end, duration, label, icon: Icon, style }) => {
  const [count, setCount] = useState(0);
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];

  useEffect(() => {
    let startTime;
    const animateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      setCount(Math.floor(end * percentage));
      if (percentage < 1) {
        requestAnimationFrame(animateCount);
      }
    };
    requestAnimationFrame(animateCount);
  }, [end, duration]);

  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1rem",
        ...style,
      }}
    >
      <Icon
        size={24}
        color={theme.accentColor}
        style={{ marginBottom: "0.5rem" }}
      />
      <div
        style={{
          fontSize: "1.25rem",
          fontWeight: "bold",
          color: theme.textColor,
          textAlign: "center",
        }}
      >
        {count}+
      </div>
      <div
        style={{
          fontSize: "0.75rem",
          color: theme.textColor,
          textAlign: "center",
        }}
      >
        {label}
      </div>
    </div>
  );
};

const CounterSection = () => {
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];
  const containerRef = useRef(null);

  const counters = [
    { end: 150, label: "Hotels", icon: Building },
    // { end: 550, label: "Investors", icon: DollarSign },  
    { end: 1000, label: "Room Count", icon: Bed },
    { end: 10, label: "Cities", icon: Building2 },
  ];

  const containerStyle = {
    backgroundColor: theme.bgColor,
    padding: "3rem 1rem",
    overflow: "hidden",
  };

  const headingStyle = {
    fontSize: "1.75rem",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "2rem",
    color: theme.textColor,
  };

  const countersContainerStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "1rem",
    justifyContent: "center",
  };

  return (
    <section style={containerStyle} ref={containerRef}>
      <h2 style={headingStyle}>Our Achievements</h2>
      <div style={countersContainerStyle}>
        {counters.map((counter, index) => (
          <AnimatedCounter
            key={index}
            end={counter.end}
            duration={2000}
            label={counter.label}
            icon={counter.icon}
          />
        ))}
      </div>
    </section>
  );
};

const TestimonialCard = ({ quote, author, rating }) => {
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];

  const cardStyle = {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    padding: "24px",
    margin: "0 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  };

  const ratingStyle = {
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
  };

  const quoteStyle = {
    color: "#4a5568",
    fontSize: "18px",
    marginBottom: "16px",
  };

  const authorStyle = {
    fontWeight: "600",
    color: "#2d3748",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      style={cardStyle}
    >
      <div style={ratingStyle}>
        {[...Array(5)].map((_, i) => (
          <span key={i} style={{ color: "#f6e05e", fontSize: "24px" }}>
            ★
          </span>
        ))}
      </div>
      <p style={quoteStyle}>"{quote}"</p>
      <p style={authorStyle}>{author}</p>
    </motion.div>
  );
};

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];

  const testimonials = [
    {
      quote:
        "Our family trip with ClubsNjoy was incredible! The entire process, from booking to the trip itself, was so smooth. They planned everything perfectly, and we got to make memories that we’ll cherish forever. Can’t wait for next year!",
      author: "Vikram Malhotra, Mumbai",
      rating: 5,
    },
    {
      quote:
        "ClubsNjoy made our annual family vacation stress-free and enjoyable. They took care of all the details, from the accommodations to the activities. Everything was so well-organized, and we didn’t have to worry about a thing!",
      author: "Anita Desai, Bangalore",
      rating: 5,
    },
    {
      quote:
        "This was our first time traveling with ClubsNjoy, and they exceeded all our expectations! The destinations were family-friendly, and the package offered great value. The kids especially loved it, and we got to relax—perfect combo!",
      author: "Karan Mehta, Delhi",
      rating: 5,
    },
    {
      quote:
        "ClubsNjoy planned the perfect family getaway for us. From the beautiful locations to the thoughtful itinerary, everything was spot on. It’s so rare to find a service that truly understands what families need while traveling.",
      author: "Divya Krishnamurthy, Hyderabad",
      rating: 5,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const sectionStyle = {
    padding: "64px 0",
    backgroundColor: theme.bgColor,
  };

  const containerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const headingStyle = {
    fontSize: "30px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "8px",
  };

  const subheadingStyle = {
    fontSize: "20px",
    textAlign: "center",
    marginBottom: "32px",
  };

  const dotsContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginTop: "32px",
  };

  const dotStyle = {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    transition: "all 0.3s",
    margin: "0 4px",
  };

  return (
    <section style={sectionStyle}>
      <div style={containerStyle}>
        <h2 style={headingStyle}>Testimonials</h2>
        <h3 style={subheadingStyle}>
          Hear the Success Stories from Our Valued Investors
        </h3>
        <div style={{ maxWidth: "800px", width: "100%" }}>
          <AnimatePresence mode="wait">
            <TestimonialCard
              key={currentTestimonial}
              {...testimonials[currentTestimonial]}
            />
          </AnimatePresence>
        </div>
        <div style={dotsContainerStyle}>
          {testimonials.map((_, index) => (
            <button
              key={index}
              style={{
                ...dotStyle,
                backgroundColor:
                  index === currentTestimonial
                    ? theme.accentColor
                    : theme.textColor,
                opacity: index === currentTestimonial ? 1 : 0.5,
              }}
              onClick={() => setCurrentTestimonial(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];
  const currentYear = new Date().getFullYear();

  const footerStyle = {
    backgroundColor: theme.bgColor,
    padding: "48px 16px",
  };

  const containerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "32px",
    width: "100%",
    marginBottom: "32px",
    textAlign: "center",
  };

  const logoStyle = {
    height: "70px",
    marginTop: "15px",
    marginBottom: "16px",
  };

  const headingStyle = {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "16px",
  };

  const listStyle = {
    listStyle: "none",
    padding: 0,
    margin: 0,
  };

  const listItemStyle = {
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const linkStyle = {
    color: "#4a5568",
    textDecoration: "none",
  };

  const socialIconsStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
  };

  const copyrightStyle = {
    borderTop: "1px solid #e2e8f0",
    paddingTop: "32px",
    fontSize: "14px",
    textAlign: "center",
    color: "#4a5568",
    paddingBottom: "15vh",
  };

  return (
    <footer style={footerStyle}>
      <div style={containerStyle}>
        <div style={gridStyle}>
          <div>
            <img src={logo} alt="NJoy Club Logo" style={logoStyle} />
          </div>
          <div>
            <h3 style={headingStyle}>Company</h3>
            <ul style={listStyle}>
              {[
                { name: "About Us", path: "/about" },
                { name: "Contact Us", path: "/contact" },
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms & Conditions", path: "/terms" },
              ].map((item) => (
                <li key={item.name} style={listItemStyle}>
                  <Link to={item.path} style={linkStyle}>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 style={headingStyle}>Contact Us</h3>
            <ul style={listStyle}>
              <li style={listItemStyle}>
                <MapPin size={20} style={{ marginRight: "12px" }} />
                <span style={{ fontSize: "14px" }}>
                  226, Lodha Signet, Kolshet Road, Thane (w) 400607,
                  Maharashtra.
                </span>
              </li>
              <li style={listItemStyle}>
                <Phone size={16} style={{ marginRight: "8px" }} />
                <span style={{ fontSize: "14px" }}>+91 9004180898</span>
              </li>
              <li style={listItemStyle}>
                <Phone size={16} style={{ marginRight: "8px" }} />
                <span style={{ fontSize: "14px" }}>+91 9082269941</span>
              </li>
              <li style={listItemStyle}>
                <Mail size={16} style={{ marginRight: "8px" }} />
                <span style={{ fontSize: "14px" }}>Info@clubnjoy.com</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 style={headingStyle}>Follow Us</h3>
            <div style={socialIconsStyle}>
              <a
                href="https://www.instagram.com/clubnjoy_official/profilecard/?igsh=MWQ0NnR5dzRkeHRhdA=="
                style={linkStyle}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
        <div style={copyrightStyle}>
          &copy; {currentYear} NJOY CLUB. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
};

export { CounterSection, Testimonials, Footer };
